import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Minus, Trash2, ShoppingCart, ArrowRight, Tag, Truck } from "lucide-react";
import { useCartStore, computeConfiguredPromo } from "../store/cartStore";
import { getCartQuote } from "../lib/api";
import FreeDeliveryBanner from "../components/FreeDeliveryBanner";
import { chf } from "../lib/format";

export default function Cart() {
  const nav = useNavigate();
  const { items, updateQuantity, removeItem } = useCartStore();
  const [quote, setQuote] = useState(null);

  // Formules calculées côté serveur (même logique que le Checkout : /api/cart/quote, sans code promo ici).
  useEffect(() => {
    if (items.length === 0) { setQuote(null); return; }
    const payload = items.map((i) => ({ id: i.id, price: i.price, quantity: i.quantity }));
    getCartQuote(payload, null).then(setQuote).catch(() => setQuote(null));
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <ShoppingCart size={56} className="mx-auto mb-4" style={{ color: "#cbd5e1" }} />
        <h1 className="text-xl font-extrabold" style={{ color: "#0b1220" }}>Votre panier est vide</h1>
        <p className="text-sm mt-2 mb-6" style={{ color: "#64748b" }}>Ajoutez des produits depuis la boutique.</p>
        <button onClick={() => nav("/boutique")} data-testid="cart-empty-shop" className="rounded-xl font-bold text-white px-6 py-3" style={{ background: "#f97316" }}>
          Découvrir la boutique
        </button>
      </div>
    );
  }

  const subtotal = quote?.subtotal ?? items.reduce((t, i) => t + i.price * i.quantity, 0);
  const secondHalf = quote?.second_half_discount || 0;
  const cfgPromo = computeConfiguredPromo(items);
  const total = Math.max(0, (quote?.total_products ?? subtotal) - cfgPromo.discount);
  const freeDelivery = !!quote?.free_delivery;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-extrabold mb-6" style={{ color: "#0b1220" }}>Mon panier</h1>

      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-3 bg-white rounded-2xl p-3" style={{ border: "1px solid #f0f0f0" }} data-testid={`cart-item-${it.id}`}>
            <div className="w-16 h-16 rounded-xl flex-shrink-0" style={{ background: "#f8fafc" }}>
              {it.image_url && <img src={it.image_url} alt={it.title} className="w-full h-full object-contain p-1" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold line-clamp-2" style={{ color: "#0b1220" }}>{it.title}</p>
              <p className="text-sm font-bold mt-1" style={{ color: "#f97316" }}>{chf(it.price)}</p>
            </div>
            <div className="flex items-center gap-2 rounded-lg px-2 py-1" style={{ border: "1px solid #e5e7eb" }}>
              <button onClick={() => updateQuantity(it.id, it.quantity - 1)} data-testid={`cart-dec-${it.id}`} style={{ color: "#0b1220" }}><Minus size={16} /></button>
              <span className="font-bold w-5 text-center text-sm">{it.quantity}</span>
              <button onClick={() => updateQuantity(it.id, it.quantity + 1)} data-testid={`cart-inc-${it.id}`} style={{ color: "#0b1220" }}><Plus size={16} /></button>
            </div>
            <button onClick={() => removeItem(it.id)} data-testid={`cart-remove-${it.id}`} className="p-2" style={{ color: "#dc2626" }}><Trash2 size={18} /></button>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl p-5 mt-6" style={{ border: "1px solid #f0f0f0" }}>
        <FreeDeliveryBanner subtotal={subtotal} freeDelivery={freeDelivery} />
        <div className="flex justify-between text-sm mb-2" style={{ color: "#475569" }}>
          <span>Sous-total</span><span className="font-semibold">{chf(subtotal)}</span>
        </div>
        {secondHalf > 0 && (
          <div className="flex justify-between text-sm mb-2" style={{ color: "#16a34a" }} data-testid="cart-promo">
            <span className="flex items-center gap-1"><Tag size={14} /> 2e article à −50 %</span><span className="font-semibold">-{chf(secondHalf)}</span>
          </div>
        )}
        {cfgPromo.lines.map((l) => (
          <div key={l.id} className="flex justify-between text-sm mb-2" style={{ color: "#16a34a" }} data-testid={`cart-cfgpromo-${l.id}`}>
            <span className="flex items-center gap-1"><Tag size={14} /> {l.title} · {l.label}</span><span className="font-semibold">-{chf(l.amount)}</span>
          </div>
        ))}
        {freeDelivery && (
          <div className="flex justify-between text-sm mb-2 font-semibold" style={{ color: "#16a34a" }} data-testid="cart-freedelivery">
            <span className="flex items-center gap-1"><Truck size={14} /> Livraison offerte</span><span>Incluse</span>
          </div>
        )}
        <div className="flex justify-between items-center mt-3 pt-3" style={{ borderTop: "1px solid #e5e7eb" }}>
          <span className="font-extrabold" style={{ color: "#0b1220" }}>Total</span>
          <span className="text-xl font-extrabold" data-testid="cart-total" style={{ color: "#0b1220" }}>{chf(total)}</span>
        </div>
        <p className="text-xs mt-2" style={{ color: "#94a3b8" }}>Frais de livraison calculés à l&apos;étape suivante.</p>
        <button onClick={() => nav("/checkout")} data-testid="cart-checkout-btn"
          className="w-full rounded-xl font-bold text-white py-3.5 mt-4 flex items-center justify-center gap-2" style={{ background: "#0b1220" }}>
          Passer commande <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}
